import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUniversalAuth } from "@wtc/ui";
import type { QuickBooksCustomer, QuickBooksInvoice } from "@/lib/types/quickbooks-unified";
import type { MultiCompanyResult } from "@/lib/integrations/quickbooks/quickbooks-multi-company-utils";
import { parseQuickBooksCustomerRef } from "@/lib/utils/crm/quickbooks-company-ref";
import { fetchCompanyById, fetchContactById } from "./company-quickbooks-graphql";
import { fetchQuickBooksCustomerData } from "./company-quickbooks-api";

export function useCompanyQuickBooksCustomer(companyId: string) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: ["company-quickbooks-customer", companyId],
    queryFn: async (): Promise<{
      companyId: string;
      companyName: string;
      quickbooksCustomerRef?: string;
      quickbooksCustomerName?: string;
      quickbooksSyncAt?: string;
      customerData?: QuickBooksCustomer[];
    }> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const company = await fetchCompanyById(companyId);
      if (!company) {
        throw new Error("Company not found");
      }

      if (!company.quickbooks_customer_ref) {
        return { companyId, companyName: company.name };
      }

      const qbRefs = parseQuickBooksCustomerRef(company.quickbooks_customer_ref);
      const customerData = await fetchQuickBooksCustomerData(qbRefs, session.access_token);

      return {
        companyId,
        companyName: company.name,
        quickbooksCustomerRef: company.quickbooks_customer_ref ?? undefined,
        quickbooksCustomerName: company.quickbooks_customer_name ?? undefined,
        quickbooksSyncAt: company.quickbooks_sync_at ?? undefined,
        customerData,
      };
    },
    enabled: !!session?.access_token && !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCompanyQuickBooksInvoices(companyId: string) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: ["company-quickbooks-invoices", companyId],
    queryFn: async (): Promise<MultiCompanyResult<QuickBooksInvoice>> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const company = await fetchCompanyById(companyId);
      if (!company) {
        throw new Error("Company not found");
      }

      if (!company.quickbooks_customer_ref) {
        return { items: [], totalCount: 0, companiesCount: 0, companies: [] };
      }

      const qbRefs = parseQuickBooksCustomerRef(company.quickbooks_customer_ref);

      const invoicePromises = qbRefs.map(async ({ customerId, realmId }) => {
        const queryParams = new URLSearchParams({
          entityType: "invoices",
          customerId: customerId,
          ...(realmId && { realmId }),
        });

        const response = await fetch(`/api/quickbooks/entities?${queryParams.toString()}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          console.warn(`Failed to fetch QB invoices for customer ${customerId}:`, response.status);
          return { items: [], totalCount: 0, companiesCount: 0, companies: {} };
        }

        return response.json();
      });

      const invoiceResults = await Promise.all(invoicePromises);

      const allItems = invoiceResults.flatMap((result) => result.items || []);
      const totalCount = invoiceResults.reduce((sum, result) => sum + (result.totalCount || 0), 0);
      const companies = invoiceResults.reduce(
        (acc, result) => {
          if (result.companies) {
            Object.assign(acc, result.companies);
          }
          return acc;
        },
        {} as Record<string, any>
      );

      return {
        items: allItems,
        totalCount,
        companiesCount: qbRefs.length,
        companies,
      };
    },
    enabled: !!session?.access_token && !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactQuickBooksData(contactId: string) {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: ["contact-quickbooks-data", contactId],
    queryFn: async (): Promise<{
      contactId: string;
      contactName: string;
      companyId?: string;
      companyName?: string;
      quickbooksCustomerRef?: string;
      quickbooksCustomerName?: string;
      quickbooksSyncAt?: string;
      customerData?: QuickBooksCustomer[];
    }> => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }
      if (!contactId) {
        throw new Error("Contact ID is required");
      }

      const contact = await fetchContactById(contactId);
      if (!contact) {
        throw new Error("Contact not found");
      }

      if (!contact.company_id) {
        return { contactId, contactName: contact.name };
      }

      const company = await fetchCompanyById(contact.company_id);
      if (!company) {
        throw new Error("Company not found");
      }

      if (!company.quickbooks_customer_ref) {
        return {
          contactId,
          contactName: contact.name,
          companyId: company.id,
          companyName: company.name,
        };
      }

      const qbRefs = parseQuickBooksCustomerRef(company.quickbooks_customer_ref);
      const customerData = await fetchQuickBooksCustomerData(qbRefs, session.access_token);

      return {
        contactId,
        contactName: contact.name,
        companyId: company.id,
        companyName: company.name,
        quickbooksCustomerRef: company.quickbooks_customer_ref ?? undefined,
        quickbooksCustomerName: company.quickbooks_customer_name ?? undefined,
        quickbooksSyncAt: company.quickbooks_sync_at ?? undefined,
        customerData,
      };
    },
    enabled: !!session?.access_token && !!contactId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSyncTimeEntryToQuickBooksViaCompany() {
  const queryClient = useQueryClient();
  const { session } = useUniversalAuth();

  return useMutation({
    mutationFn: async ({ timeEntryId, companyId }: { timeEntryId: string; companyId: string }) => {
      if (!session?.access_token) {
        throw new Error("Authentication token not available");
      }

      const company = await fetchCompanyById(companyId);
      if (!company) {
        throw new Error("Company not found");
      }

      if (!company.quickbooks_customer_ref) {
        throw new Error("Company has no QuickBooks customer mapping");
      }

      const firstQbRef = company.quickbooks_customer_ref.split(",")[0].trim();
      const [customerId, realmId] = firstQbRef.split(";");

      const response = await fetch("/api/quickbooks/time-activities", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          timeEntryId,
          customerId,
          realmId,
          action: "sync",
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to sync time entry");
      }

      return response.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["timeEntries"] });
      void queryClient.invalidateQueries({ queryKey: ["company-quickbooks-customer"] });
      toast.success("Time entry synced to QuickBooks successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to sync time entry: ${error.message}`);
    },
  });
}
